import React, { memo, useEffect, useState } from 'react';
import { size, get } from 'lodash';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import hasAccess, { editChargerDetailsAccessId } from '../../../common/services/HasAccess';
import PlazaController from '../../../common/controllers/plaza/PlazaController';
import Checkbox from '../../../common/components/Checkbox/Checkbox';
import Select from '../../../common/components/Select/Select';
import dictionariesTypesEnum from '../../../common/constants/dictionaries';
import Customer from '../../../common/resources/Customer';
import { CustomerChargerLinkField } from '../../ChargerDetails/components/ChargerDetailsFormRenderers/renderReadOnlyTextField';
import { getFilteredPlazasOptions } from '../../../common/components/Form/RenderPlazaSelect/component';

const TableSelect = memo(({ onChange, value, options, displayEmpty = true, multiple = false, maxListHeight }) => (
  <Select
    multiple={multiple}
    value={value}
    autoWidth
    fullWidth
    className="table-material-text"
    onChange={(event) => {
      onChange(event.target.value);
    }}
    displayEmpty={displayEmpty}
    maxListHeight={maxListHeight}
  >
    {options}
  </Select>
));

const cellCenterStyle = { textAlign: 'center' };

const CheckboxCell = memo(({ id, activeChargerIds, onRowSelected, selectAll }) => (
  <Checkbox
    noPadding
    disabled={selectAll}
    checked={(size(activeChargerIds) ? activeChargerIds.includes(id) : false) || selectAll}
    onChange={() => onRowSelected(id)}
  />
));

const IdCell = memo(({ value, permissions, source }) => {
  const sourceParam = source ? `?source=${source}` : '';
  const link = hasAccess(editChargerDetailsAccessId, permissions)
    ? `/chargers/edit/${value}/details/${sourceParam}`
    : `/chargers/details/${value}/details/${sourceParam}`;

  return (
    <Link to={link} className="link">
      {value}
    </Link>
  );
});

export const combineUrls = (startUrl, endUrl) => {
  const start = startUrl.endsWith('/') ? startUrl.slice(0, -1) : startUrl;
  const end = endUrl.startsWith('/') ? endUrl.slice(1, -1) : endUrl;

  return `${start}/${end}`;
};

const CustomerURLCell = memo(({ customer, chargerId, value, user }) => {
  if (user.customer_id === customer.customerId) {
    return <div style={cellCenterStyle}>{value}</div>;
  }

  return <CustomerChargerLinkField customer={customer} chargerId={chargerId} user={user} />;
});

const PlazaTableSelect = ({ value, onChange, displayEmpty }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    getFilteredPlazasOptions().then(({ plazas }) => {
      const opts = PlazaController.makeMenuOptionsFromBasicOptions(plazas);

      setOptions(opts);
    });
  }, []);

  return <TableSelect onChange={onChange} value={value} options={options} displayEmpty={displayEmpty} />;
};

// Customers Select

const CustomerTableSelect = ({ value, onChange, displayEmpty }) => {
  const dictionary = useSelector((state) => get(state.dictionaries, [dictionariesTypesEnum.customers, 'dictionary']));
  const [options, setOptions] = useState([]);

  const getCustomers = async () => {
    try {
      const response = await Customer.getAll();
      const customers = get(response, 'data.payloads') || [];
      const mappedCustomers = Customer.toSelectOptions(customers);

      setOptions(Customer.toSelect(mappedCustomers));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!dictionary) {
      getCustomers();
    } else {
      const mappedCustomers = Customer.toSelectOptions(dictionary);

      setOptions(Customer.toSelect(mappedCustomers));
    }
  }, [dictionary]);

  return <TableSelect onChange={onChange} value={value} options={options} displayEmpty={displayEmpty} />;
};

export { TableSelect, IdCell, CheckboxCell, PlazaTableSelect, CustomerURLCell, CustomerTableSelect };
